// src/components/EdgeRenderer.jsx
import React, { useMemo } from "react";
import registry from "./edgetypes";
import { getEdgeVisualStyle } from "../utility/edgeStyle";

export default function EdgeRenderer(props) {
  const {
    edge,
    nodeById,
    edgeIndex,
    selectedIndex,
    edgeControls,
    onStartEdgeDrag,
    hoverEdgeIndex,
    setHoverEdgeIndex,
    showCriticalPath = false,
    isInChain = false,
    chainActive = false,
    onSelectEdge,
    opacity: externalOpacity = 1,
    ...rest
  } = props;

  // Determine if both nodes are critical (totalFloat = 0)
  const fromNode = nodeById[edge.from];
  const toNode = nodeById[edge.to];

  const isCriticalEdge = useMemo(() => {
    return (
      showCriticalPath &&
      fromNode &&
      toNode &&
      fromNode.totalFloat === 0 &&
      toNode.totalFloat === 0
    );
  }, [showCriticalPath, fromNode, toNode]);

  // ========== UNIFIED OPACITY LOGIC ==========
  const edgeOpacity = useMemo(() => {
    if (isInChain) return 1.0;
    if (chainActive) return 0.15;
    if (showCriticalPath) return isCriticalEdge ? 1.0 : 0.3;
    if (externalOpacity < 1) return externalOpacity;
    return 1.0;
  }, [isInChain, chainActive, showCriticalPath, isCriticalEdge, externalOpacity]);

  // Determine type (fallback to orthogonal)
  const type = (edge.type || "orthogonal").toLowerCase();

  // Choose edge component
  const Comp = registry[type] || registry["orthogonal"];
  const isSubTaskEdge = edge.type === "hierarchy";
  // Choose color - CRITICAL EDGES GET RED
  const { color, strokeDasharray, strokeWidth } = getEdgeVisualStyle(
    fromNode,
    toNode,
    { isCriticalEdge, isInChain, isSubTaskEdge },
  );

  // Determine if this edge is selected
  const isSelected = selectedIndex === edgeIndex;

  // Determine if this edge is hovered
  const isHovered = hoverEdgeIndex === edgeIndex;

  // Handle edge selection
  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelectEdge) {
      onSelectEdge(edgeIndex);
    }
  };

  return (
    <Comp
      {...rest}
      edge={edge}
      nodeById={nodeById}
      edgeIndex={edgeIndex}
      selectedIndex={selectedIndex}
      edgeControls={edgeControls}
      onStartEdgeDrag={onStartEdgeDrag}
      color={color}
      strokeDasharray={strokeDasharray}
      strokeWidth={strokeWidth}
      isCriticalEdge={isCriticalEdge}
      isInChain={isInChain}
      isSelected={isSelected}
      isHovered={isHovered}
      opacity={edgeOpacity}
      onClick={handleClick}
      onHoverEnter={() => setHoverEdgeIndex && setHoverEdgeIndex(edgeIndex)}
      onHoverLeave={() => setHoverEdgeIndex && setHoverEdgeIndex(null)}
    />
  );
}